import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  UserIcon,
  CameraIcon,
  CheckIcon,
  PencilIcon,
  CalendarIcon,
  BookOpenIcon,
  ShareIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

import { userService } from '../../services/user';

const Profile = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm({
    mode: 'onChange'
  });

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await userService.getProfile();
        setProfileData(response.data);
        
        // Set form values
        setValue('name', response.data.user.name);
        setValue('bio', response.data.user.bio || '');
        setValue('phone', response.data.user.phone || '');
        
        setLoading(false);
      } catch (error) {
        toast.error('Failed to load profile');
        setLoading(false);
      }
    };

    fetchProfile();
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      await userService.updateProfile(data);
      toast.success('Profile updated successfully');
      setIsEditing(false);
      
      // Refresh profile data
      const response = await userService.getProfile();
      setProfileData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await userService.uploadAvatar(formData);
      setProfileData(prev => ({
        ...prev,
        user: { ...prev.user, avatarUrl: response.data.avatarUrl }
      }));
      toast.success('Avatar updated successfully');
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-500 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-serif font-bold mb-2">Your Profile</h1>
            <p className="text-primary-100 text-lg">
              Manage your personal information and preferences
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="card p-6 text-center">
              {/* Avatar */}
              <div className="relative inline-block mb-4">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 mx-auto">
                  {profileData.user.avatarUrl ? (
                    <img
                      src={profileData.user.avatarUrl}
                      alt={profileData.user.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <UserIcon className="w-16 h-16 text-gray-400" />
                    </div>
                  )}
                </div>
                
                {/* Upload Button */}
                <label className="absolute bottom-0 right-0 bg-primary-600 text-white p-2 rounded-full cursor-pointer hover:bg-primary-700 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    className="hidden"
                    disabled={isUploading}
                  />
                  {isUploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <CameraIcon className="w-4 h-4" />
                  )}
                </label>
              </div>

              <h2 className="text-2xl font-serif font-bold text-ink-black mb-2">
                {profileData.user.name}
              </h2>
              <p className="text-gray-600 mb-4">{profileData.user.email}</p>
              
              {profileData.user.bio && (
                <p className="text-gray-700 text-sm mb-4 italic">
                  "{profileData.user.bio}"
                </p>
              )}

              <div className="flex items-center justify-center text-sm text-gray-500">
                <CalendarIcon className="w-4 h-4 mr-1" />
                <span>
                  Member since {new Date(profileData.stats.memberSince).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="card p-6 mt-6">
              <h3 className="text-lg font-semibold text-ink-black mb-4">
                Your Statistics
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <BookOpenIcon className="w-5 h-5 text-primary-600" />
                    <span className="text-gray-700">Total Entries</span>
                  </div>
                  <span className="font-semibold text-ink-black">
                    {profileData.stats.totalEntries}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <ShareIcon className="w-5 h-5 text-blue-600" />
                    <span className="text-gray-700">Shared Entries</span>
                  </div>
                  <span className="font-semibold text-ink-black">
                    {profileData.stats.sharedEntries}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Profile Form */}
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-ink-black">
                  Personal Information
                </h3>
                {!isEditing ? (
                  <motion.button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 transition-colors"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <PencilIcon className="w-4 h-4" />
                    <span>Edit</span>
                  </motion.button>
                ) : (
                  <div className="flex space-x-2">
                    <motion.button
                      onClick={() => setIsEditing(false)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      onClick={handleSubmit(onSubmit)}
                      disabled={isSubmitting}
                      className="btn-primary flex items-center space-x-2"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <CheckIcon className="w-4 h-4" />
                          <span>Save</span>
                        </>
                      )}
                    </motion.button>
                  </div>
                )}
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Name must be at least 2 characters' },
                      maxLength: { value: 50, message: 'Name cannot exceed 50 characters' }
                    })}
                    type="text"
                    disabled={!isEditing}
                    className={`input-field ${!isEditing ? 'bg-gray-50' : ''} ${
                      errors.name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                  />
                  {errors.name && (
                    <motion.p
                      className="mt-1 text-sm text-red-600"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.name.message}
                    </motion.p>
                  )}
                </div>

                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={profileData.user.email}
                    disabled
                    className="input-field bg-gray-50 cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Email cannot be changed. Contact support if needed.
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    {...register('phone', {
                      pattern: {
                        value: /^[+]?[\d\s\-\(\)]+$/,
                        message: 'Please enter a valid phone number'
                      }
                    })}
                    type="tel"
                    disabled={!isEditing}
                    placeholder="Enter your phone number"
                    className={`input-field ${!isEditing ? 'bg-gray-50' : ''} ${
                      errors.phone ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                  />
                  {errors.phone && (
                    <motion.p
                      className="mt-1 text-sm text-red-600"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      {errors.phone.message}
                    </motion.p>
                  )}
                </div>

                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bio
                  </label>
                  <textarea
                    {...register('bio', {
                      maxLength: { value: 500, message: 'Bio cannot exceed 500 characters' }
                    })}
                    disabled={!isEditing}
                    rows={4}
                    placeholder="Tell us a bit about yourself..."
                    className={`input-field resize-none ${!isEditing ? 'bg-gray-50' : ''} ${
                      errors.bio ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                  />
                  <div className="flex justify-between mt-1">
                    {errors.bio && (
                      <motion.p
                        className="text-sm text-red-600"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        {errors.bio.message}
                      </motion.p>
                    )}
                    <p className="text-xs text-gray-500 ml-auto">
                      {watch('bio')?.length || 0}/500 characters
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;